const UNRANKED_PRIORITY = Number.MAX_SAFE_INTEGER;
// One below UNRANKED_PRIORITY so the sticky audience sorts ahead of other
// unranked experiences, but still behind anything with an explicit priority.
const STICKY_UNRANKED_PRIORITY = UNRANKED_PRIORITY - 1;

const resolvePriority = (
  audienceId: string | undefined,
  priorityByAudienceId: Record<string, number>,
  stickyAudienceId: string | null | undefined
): number => {
  if (!audienceId) {
    return UNRANKED_PRIORITY;
  }

  const priority = priorityByAudienceId[audienceId];

  if (typeof priority === 'number') {
    return priority;
  }

  return audienceId === stickyAudienceId
    ? STICKY_UNRANKED_PRIORITY
    : UNRANKED_PRIORITY;
};

/**
 * Sorts experiences ascending by the priority configured for their audience.
 * Experiences without a resolvable priority are treated as unranked and kept
 * after ranked ones. `Array.prototype.sort` is stable since ES2019, so when
 * the priority map is empty (or none of the audiences resolve), every
 * experience maps to the same unranked value and the original array order
 * (i.e. the order the Ninetailed/Contentful response arrived in) is
 * preserved unchanged — this is the required no-priority-configured fallback.
 *
 * `stickyAudienceId` is an additional, optional fallback layer for audiences
 * that have no explicit priority: if it matches one of the (otherwise
 * unranked) experiences, that experience is moved ahead of the other
 * unranked ones. This lets the caller pin whichever audience already "won"
 * for an earlier component on the same page, so every other component that
 * also references that audience resolves to the same one — instead of each
 * component's own `nt_experiences` array order deciding independently.
 */
export const sortExperiencesByAudiencePriority = <
  T extends { audience?: { id: string } | null }
>(
  experiences: T[],
  priorityByAudienceId: Record<string, number>,
  stickyAudienceId?: string | null
): T[] => {
  return [...experiences].sort(
    (a, b) =>
      resolvePriority(a.audience?.id, priorityByAudienceId, stickyAudienceId) -
      resolvePriority(b.audience?.id, priorityByAudienceId, stickyAudienceId)
  );
};
